import React from "react";
import {
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Document,
  pdf,
} from "@react-pdf/renderer";

// ---------- Helpers ----------
// Support both shapes: {caseAuthNumber} or {authorizationNumber}, etc.
const normalizeCaseInfo = (invoice) => {
  const c = invoice?.case || invoice?.inPatientCase || {};
  return {
    authNumber:
      c.caseAuthNumber || c.authorizationNumber || null,
    referringDoctor:
      c.referringDoctorName || c.caseReferringDoctor || null,
    referringBHF:
      c.referringDoctorBHF || c.caseBHF || null,
    referringHPCSA:
      c.referringDoctorHPCSA || c.caseHPCSA || null,
  };
};


const money = (x) => `R ${Number(x ?? 0).toFixed(2)}`;

// Accepts string | object | array of either
const formatModifiers = (modifier) => {
  if (modifier == null) return "N/A";
  const arr = Array.isArray(modifier) ? modifier : [modifier];

  const codes = arr
    .map((m) => {
      if (m == null) return null;
      if (typeof m === "string" || typeof m === "number") return String(m);
      if (typeof m === "object") return m.code ?? null;
      return null;
    })
    .filter(Boolean);

  return codes.length ? codes.join(", ") : "N/A";
};

// Extract "HH:mm" from an ISO-ish string, or fall back to last 5 chars if needed
const extractTimeHHmm = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const d = new Date(value);
    if (!isNaN(d)) {
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${hh}:${mm}`;
    }
    const m = value.match(/(\d{2}:\d{2})$/);
    if (m) return m[1];
    return value.slice(-5);
  }
  return "";
};

// Column widths (keep header and rows in sync)
const COL_DESC = "52%";
const COL_MOD  = "14%";
const COL_PAT  = "12%";
const COL_MA   = "12%";
const COL_TOT  = "10%";
const COL_NUM  = "10%";

const styles = StyleSheet.create({
  page: { padding: 20, fontSize: 10 },
  logo: { height: 75, width: 150, marginBottom: 10 },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  practiceName: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 4,
  },
  title: {
    textAlign: "center",
    fontSize: 14,
    fontWeight: "bold",
    marginVertical: 8,
    textTransform: "uppercase",
  },
  section: { marginBottom: 6 },
  line: { height: 1, backgroundColor: "#000", marginVertical: 6 },

  tableHeader: {
    flexDirection: "row",
    borderBottom: 1,
    fontWeight: "bold",
    paddingBottom: 2,
    marginTop: 6,
  },
  hDesc: { width: COL_DESC, paddingRight: 4 },
  hMod:  { width: COL_MOD,  textAlign: "left" },
  hNum:  { width: COL_NUM,  textAlign: "right" },

  row:  { flexDirection: "row", marginVertical: 2 },
  cDesc: { width: COL_DESC, paddingRight: 4 },
  cMod:  { width: COL_MOD,  textAlign: "left" },
  cNum:  { width: COL_NUM,  textAlign: "right" },

  bold: { fontWeight: "bold" },

  totalsWrap: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 4,
  },
  totalsLabels: { width: "25%" },
  totalsValues: { width: "15%" },

  patientHeader: {
    marginTop: 8,
    marginBottom: 4,
    fontWeight: "bold",
    fontSize: 10,
  },
  italics: { fontStyle: "italic" },

  metaBlock: { marginTop: 6, marginBottom: 6 },
  metaLine:  { fontSize: 9, lineHeight: 1.25 }, // small & uniform


});

const InvoicePDF = ({ invoice }) => {
  const {
    account,
    practice,
    invoiceNumber,
    invoiceDate,
    lines = [],
    totalAmount = 0,
    totalPatientAmount = 0,
    totalMedicalAidAmount = 0,
    practitioner = {},
    medicalAid = {},
    placeOfService,
    case: invoiceCase,
  } = invoice;

  const isVatRegistered = !!practice?.vatNumber;
  const vatAmount = isVatRegistered ? Number(totalAmount) * 0.15 : 0;
  const titleText = isVatRegistered ? "Tax Invoice" : "Invoice";
  const caseInfo = normalizeCaseInfo(invoice);
// Build a single compact line like:
// "The patient was referred by Dr Jane (BHF: 123, HPCSA: 456) | Authorization 123456 applies"
const buildRefLine = (caseInfo) => {
  if (!caseInfo) return "";
  const bits = [];

  if (caseInfo.referringDoctor) {
    const regs = [];
    if (caseInfo.referringBHF) regs.push(`BHF: ${caseInfo.referringBHF}`);
    if (caseInfo.referringHPCSA) regs.push(`HPCSA: ${caseInfo.referringHPCSA}`);

    let by = `The patient was referred by ${caseInfo.referringDoctor}`;
    if (regs.length) by += ` (${regs.join(", ")})`;
    bits.push(by);
  }

  if (caseInfo.authNumber) {
    bits.push(`Authorization ${caseInfo.authNumber} applies`);
  }

  return bits.join(" | ");
};


  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          {!!practice?.logoURL && (
            <Image
              style={[styles.logo, { maxHeight: 150, width: "auto" }]}
              src={practice.logoURL}
            />
          )}
          <View>
            <Text style={styles.practiceName}>{practice?.practiceName}</Text>

            <Text>
              <Text style={styles.bold}>Practice No: </Text>
              {practice?.practiceNumber}
            </Text>
            <Text>
              <Text style={styles.bold}>HPCSA No: </Text>
              {practice?.hpcsaNumber}
            </Text>

            {isVatRegistered && (
              <Text>
                <Text style={styles.bold}>VAT: </Text>
                {practice?.vatNumber}
              </Text>
            )}

            <View style={styles.line} />

            <Text>
              {practice?.practiceAddress} {practice?.practiceAddress2}
            </Text>
            <Text>
              {practice?.practiceCity} {practice?.practicePostalCode}
            </Text>

            <View
              style={[
                styles.section,
                { flexDirection: "row", justifyContent: "space-between" },
              ]}
            />

            <Text>
              <Text style={styles.bold}>Email: </Text>
              {practice?.practiceEmail}
            </Text>
            <Text>
              <Text style={styles.bold}>Tel: </Text>
              {practice?.practicePhoneNumber}
            </Text>
          </View>
        </View>

        {/* Title */}
        <Text style={styles.title}>{titleText}</Text>
        <Text style={styles.line} />

        {/* Invoice Info */}
        <View
          style={[
            styles.section,
            { flexDirection: "row", justifyContent: "space-between" },
          ]}
        >
          <Text style={styles.bold}>Invoice #: {invoiceNumber}</Text>
          <Text>Date: {invoiceDate}</Text>
        </View>

        <View
          style={[
            styles.section,
            { flexDirection: "row", justifyContent: "space-between" },
          ]}
        >
          {/* Left: Account */}
          <View style={{ width: "60%" }}>
            <Text style={styles.bold}>Billed To:</Text>
            <Text>
              {account?.accountFullNames || account?.accountName || ""}{" "}
              {account?.accountLastName || ""}
            </Text>
            <Text>ACC #: {account?.accountNumber || ""}</Text>
            <Text>
              {account?.accountHomeAddress1 || ""}{" "}
              {account?.accountHomeAddress2 || ""}
            </Text>
            <Text>
              {account?.accountHomeAddressCity || ""}{" "}
              {account?.accountHomeAddressPostalCode || ""}
            </Text>

            <View
              style={[
                styles.section,
                { flexDirection: "row", justifyContent: "space-between" },
              ]}
            />

            <Text>
              <Text style={styles.bold}>Tel: </Text>
              {account?.accountTelNumberCell || ""}
            </Text>
            <Text>
              <Text style={styles.bold}>Email: </Text>
              {account?.accountEmail || ""}
            </Text>
          </View>

          {/* Right: Medical Aid */}
         <View style={{ width: "40%", alignItems: "flex-end" }}>
  {medicalAid?.name ? (
    <>
      <Text style={styles.bold}>Medical Aid</Text>
      <Text>{medicalAid.name}</Text>
      <Text>{medicalAid.number || ""}</Text>
      <Text>
        {medicalAid.option || ""}
        {medicalAid.option && medicalAid.plan && " | "}
        {medicalAid.plan || ""}
      </Text>
    </>
  ) : account?.accountIODFund ? (
    <>
      <Text style={styles.bold}>IOD</Text>

      <Text>{account.accountIODFund}</Text>

      {(account.accountIODPlan || account.accountIODOption) && (
        <Text>
          {account.accountIODPlan || ""}
          {account.accountIODPlan && account.accountIODOption && " | "}
          {account.accountIODOption || ""}
        </Text>
      )}

      {account.accountIODClaimRefNumber && (
        <Text>
          <Text style={styles.bold}>Claim Ref: </Text>
          <Text>{account.accountIODClaimRefNumber}</Text>
        </Text>
      )}

      {account.accountIODEmployer && (
        <Text>
          <Text style={styles.bold}>Employer: </Text>
          <Text>{account.accountIODEmployer}</Text>
        </Text>
      )}

      {account.accountIODEmployerWCARegNum && (
        <Text>
          <Text style={styles.bold}>WCA Reg #: </Text>
          <Text>{account.accountIODEmployerWCARegNum}</Text>
        </Text>
      )}
      {account.accountIODDateOfInjury && (
        <Text>
          <Text style={styles.bold}>Date of Injury: </Text>
          <Text>{account.accountIODDateOfInjury}</Text>
        </Text>
      )}

      {/* ✅ Spacer so it doesn't feel cramped */}
      <View style={{ height: 6 }} />
    </>
  ) : (
    <>
      <Text style={styles.bold}>Private</Text>
      <Text>Private Patient</Text>
    </>
  )}
</View>

        </View>

        {/* Headings */}
        <View style={styles.tableHeader}>
          <Text style={{ width: COL_DESC, paddingRight: 4 }}>
            Tarrif | Nappi - Description
          </Text>
          <Text style={{ width: COL_MOD, textAlign: "left" }}>Modifier</Text>
          <Text style={{ width: COL_PAT, textAlign: "right" }}>Patient</Text>
          <Text style={{ width: COL_MA,  textAlign: "right" }}>Medical Aid</Text>
          <Text style={{ width: COL_TOT, textAlign: "right" }}>Total</Text>
        </View>

        {/* NEW: Authorization sentence ABOVE practitioner detail */}
        {!!caseInfo.authNumber && (
  <View style={styles.metaBlock}>
  {(() => {
    const refLine = buildRefLine(caseInfo);
    return refLine ? <Text style={styles.metaLine}>{refLine}</Text> : null;
  })()}

  <Text style={styles.metaLine}>
    Practitioner: {practitioner?.firstName} {practitioner?.lastName} (HPCSA: {practitioner?.hpcsa})
    {" "} | Place of Service: {placeOfService}
  </Text>
</View>
        )}

        {/* Lines grouped by patient */}
        {Object.values(
          lines.reduce((acc, line) => {
            const patientId =
              line?.patient?.patientId ||
              invoice?.patient?.patientId ||
              "unknown";
            if (!acc[patientId]) acc[patientId] = [];
            acc[patientId].push(line);
            return acc;
          }, {})
        ).map((patientLines, i) => {
          const firstLine = patientLines[0];
          const patient = firstLine?.patient || invoice?.patient;

          return (
            <View key={i} style={{ marginBottom: 10 }}>
              <Text style={styles.patientHeader}>
                ({patient?.patientMedicalAidDependantCode || "N/A"}){" "}
                {patient?.patientFirstName || patient?.patientFullNames || ""}{" "}
                {patient?.patientLastName || ""}
              </Text>

              {patientLines.map((line, idx) => {
                const descText =
                  line?.selectedService?.servicesDescription ||
                  line?.selectedTariff?.description ||
                  line?.description ||
                  "";

                // Build date + start time
                const dateText =
                  line?.appointmentDateSet ||
                  line?.appointmentDateCustom ||
                  "";
                const startTime = extractTimeHHmm(
                  line?.appointmentStartSet || line?.appointmentCustomStart || ""
                );
                const dateWithTime = startTime
                  ? `${dateText} ${startTime}`
                  : dateText;

                const icd =
                  line?.icd101 || line?.icd10
                    ? ` | ICD10: ${line?.icd101 || line?.icd10}${
                        line?.icd102 ? `, ${line.icd102}` : ""
                      }`
                    : "";

                return (
                  <View key={idx} style={styles.row}>
                    {/* Description (date+time on first line, then code + description + ICD) */}
                    <View style={{ width: COL_DESC, paddingRight: 4 }}>
                      <Text style={styles.bold}>{dateWithTime}</Text>
                      <Text>
                        {line?.tariffCode || line?.nappiCode || ""} - {descText}
                        <Text>{icd}</Text>
                      </Text>
                    </View>

                    {/* Modifiers (supports arrays & objects) */}
                    <View style={{ width: COL_MOD, textAlign: "left" }}>
                      <Text>{formatModifiers(line?.modifier)}</Text>
                    </View>

                    {/* Amounts */}
                    <View style={{ width: COL_PAT, textAlign: "right" }}>
                      <Text>{money(line?.patientAmount)}</Text>
                    </View>
                    <View style={{ width: COL_MA, textAlign: "right" }}>
                      <Text>{money(line?.medAidAmount)}</Text>
                    </View>
                    <View style={{ width: COL_TOT, textAlign: "right" }}>
                      <Text>{money(line?.rate)}</Text>
                    </View>
                  </View>
                );
              })}
            </View>
          );
        })}

        <Text style={styles.line} />

        {/* Totals */}
        <View style={styles.totalsWrap}>
          {/* spacer */}
          <View style={{ width: "60%" }} />

          {/* labels */}
          <View style={styles.totalsLabels}>
            {isVatRegistered && (
              <>
                <Text style={{ fontSize: 10, textAlign: "right" }}>
                  VAT (15%):
                </Text>
                <View
                  style={{
                    height: 1,
                    backgroundColor: "#ccc",
                    marginVertical: 4,
                  }}
                />
              </>
            )}
            <Text
              style={{ fontSize: 10, fontWeight: "bold", textAlign: "right" }}
            >
              Medical Aid Total:
            </Text>
            <Text
              style={{ fontSize: 10, fontWeight: "bold", textAlign: "right" }}
            >
              Patient Total:
            </Text>
            <View
              style={{ height: 1, backgroundColor: "#000", marginVertical: 6 }}
            />
            <Text
              style={{ fontSize: 10, fontWeight: "bold", textAlign: "right" }}
            >
              Invoice Total:
            </Text>
          </View>

          {/* values */}
          <View style={styles.totalsValues}>
            {isVatRegistered && (
              <>
                <Text style={{ fontSize: 10, textAlign: "right" }}>
                  {money(vatAmount)}
                </Text>
                <View
                  style={{
                    height: 1,
                    backgroundColor: "#ccc",
                    marginVertical: 4,
                  }}
                />
              </>
            )}
            <Text
              style={{ fontSize: 10, fontWeight: "bold", textAlign: "right" }}
            >
              {money(totalMedicalAidAmount)}
            </Text>
            <Text
              style={{ fontSize: 10, fontWeight: "bold", textAlign: "right" }}
            >
              {money(totalPatientAmount)}
            </Text>
            <View
              style={{ height: 1, backgroundColor: "#000", marginVertical: 6 }}
            />
            <Text
              style={{ fontSize: 10, fontWeight: "bold", textAlign: "right" }}
            >
              {money(totalAmount)}
            </Text>
          </View>
        </View>

        {/* Banking box */}
        <View
          style={{
            marginTop: 20,
            borderWidth: 1,
            borderColor: "#000",
            padding: 8,
            borderRadius: 4,
          }}
        >
          <Text>
            The patient outstanding amount can be paid directly into the
            following bank account. Please use your account number:{" "}
            {invoice?.account?.accountNumber || "N/A"} as the reference:
          </Text>

          <View style={{ marginTop: 10, width: "60%", paddingLeft: 6 }}>
            {[
              { label: "Bank", value: invoice?.banking?.[0]?.bank?.bankName },
              {
                label: "Account Name",
                value: invoice?.banking?.[0]?.bank?.accountName,
              },
              {
                label: "Account Number",
                value: invoice?.banking?.[0]?.bank?.accountNumber,
              },
              {
                label: "Branch Code",
                value: invoice?.banking?.[0]?.bank?.branchCode,
              },
              {
                label: "Account Type",
                value: invoice?.banking?.[0]?.bank?.accountType,
              },
            ].map((item, idx) => (
              <View key={idx} style={{ flexDirection: "row", marginBottom: 2 }}>
                <Text style={{ width: "40%", fontWeight: "bold" }}>
                  {item.label}:
                </Text>
                <Text style={{ width: "60%" }}>{item.value || "N/A"}</Text>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};

// Exported PDF generator function
export async function ExportPDFInvoice(invoice, { asBlob = false } = {}) {
  try {
    const blob = await pdf(<InvoicePDF invoice={invoice} />).toBlob();

    if (asBlob) return blob;

    const blobURL = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobURL;
    link.download = `Invoice_${invoice?.invoiceNumber || "Untitled"}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobURL);
  } catch (error) {
    console.error("Error generating invoice PDF:", error);
  }
}
